package web.fstech.onthecoffee.app

import kotlinx.browser.window
import web.fstech.onthecoffee.SupabaseService
import web.fstech.onthecoffee.mvi.MviViewModel
import web.fstech.onthecoffee.mvi.UiEffect
import web.fstech.onthecoffee.mvi.UiIntent
import web.fstech.onthecoffee.mvi.UiState
import web.fstech.onthecoffee.privacy.AppLanguage
import kotlin.js.ExperimentalWasmJsInterop

enum class AppRoute(val routeName: String, val urlPath: String) {
    HOME("home", "/"),
    PRIVACY("privacy", "/privacy_policy"),
    SIGN_IN("signin", "/signin"),
    SIGN_UP("signup", "/signup"),
    FORGOT("forgot", "/forgot_password"),
    DELETE("delete", "/delete_account");

    companion object {
        fun fromRouteName(name: String): AppRoute {
            val route = entries.find { it.routeName == name } ?: HOME
            if (route == DELETE && !SupabaseService.isSignedIn()) {
                return SIGN_IN
            }
            return route
        }

        @OptIn(ExperimentalWasmJsInterop::class)
        fun fromLocation(): AppRoute {
            val path = window.location.pathname.lowercase()
            val hash = window.location.hash.lowercase()
            val route = when {
                path.contains("privacy") || hash.contains("privacy") -> PRIVACY
                path.contains("signin") || hash.contains("signin") -> SIGN_IN
                path.contains("signup") || hash.contains("signup") -> SIGN_UP
                path.contains("forgot") || hash.contains("forgot") -> FORGOT
                path.contains("delete") || hash.contains("delete") -> DELETE
                else -> HOME
            }
            if (route == DELETE && !SupabaseService.isSignedIn()) {
                return SIGN_IN
            }
            return route
        }
    }
}

data class AppState(
    val currentRoute: AppRoute = AppRoute.fromLocation(),
    val language: AppLanguage = AppLanguage.ENGLISH
) : UiState

sealed interface AppIntent : UiIntent {
    data class NavigateTo(val routeName: String) : AppIntent
    data class ChangeLanguage(val language: AppLanguage) : AppIntent
    data object SyncFromWindowLocation : AppIntent
}

sealed interface AppEffect : UiEffect

class AppViewModel : MviViewModel<AppState, AppIntent, AppEffect>(AppState()) {

    override fun handleIntent(intent: AppIntent) {
        when (intent) {
            is AppIntent.NavigateTo -> {
                val target = AppRoute.fromRouteName(intent.routeName)
                if (currentState.currentRoute != target) {
                    updateState { copy(currentRoute = target) }
                    syncBrowserUrl(target)
                }
            }
            is AppIntent.ChangeLanguage -> {
                if (currentState.language != intent.language) {
                    updateState { copy(language = intent.language) }
                }
            }
            AppIntent.SyncFromWindowLocation -> {
                val newRoute = AppRoute.fromLocation()
                if (currentState.currentRoute != newRoute) {
                    updateState { copy(currentRoute = newRoute) }
                    syncBrowserUrl(newRoute)
                }
            }
        }
    }

    @OptIn(ExperimentalWasmJsInterop::class)
    private fun syncBrowserUrl(target: AppRoute) {
        val currentLoc = window.location.pathname + window.location.hash
        if ((currentLoc != target.urlPath) && !currentLoc.startsWith("${target.urlPath}#")) {
            window.history.pushState(null, "", target.urlPath)
        }
    }
}
